import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { getFullImageUrl } from "../../utils/imageHandler";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../components/ui/tabs";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
	Briefcase,
	Heart,
	Clock,
	CheckCircle,
	XCircle,
	AlertCircle,
	Loader2,
	Trash2,
	Eye,
	MapPin,
	Building2,
	Calendar,
	TrendingUp,
} from "lucide-react";
import api from "../../api/client";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import Loader from "../../components/ui/Loader";
import AnimatedBackground from "../../components/ui/AnimatedBackground";

export default function ApplierDashboard() {
	const navigate = useNavigate();

	const [applications, setApplications] = useState([]);
	const [savedJobs, setSavedJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [actionLoading, setActionLoading] = useState({});

	useEffect(() => {
		fetchDashboardData();
	}, []);

	const fetchDashboardData = async () => {
		try {
			const [appsRes, savedRes] = await Promise.all([
				api.get("/applier/applications"),
				api.get("/applier/saved"),
			]);

			setApplications(appsRes.data || []);
			setSavedJobs(savedRes.data || []);
		} catch (err) {
			console.error(err);
			setError("Failed to load dashboard. Please try again later.");
		} finally {
			setLoading(false);
		}
	};

	const handleWithdraw = async (appId) => {
		setActionLoading((prev) => ({ ...prev, [appId]: true }));
		try {
			await api.delete(`/applier/applications/${appId}`);
			setApplications((prev) => prev.filter((a) => a.id !== appId));
		} catch (err) {
			console.error(err);
			setError(
				err.response?.data?.message ||
				err.message ||
				"Failed to withdraw application. Please try again."
			);
		} finally {
			setActionLoading((prev) => ({ ...prev, [appId]: false }));
		}
	};

	const handleRemoveSaved = async (jobId) => {
		setActionLoading((prev) => ({ ...prev, [`save-${jobId}`]: true }));
		try {
			await api.delete(`/applier/save/${jobId}`);
			setSavedJobs((prev) => prev.filter((j) => j.id !== jobId));
		} catch (err) {
			console.error(err);
			setError("Failed to remove saved job. Please try again.");
		} finally {
			setActionLoading((prev) => ({ ...prev, [`save-${jobId}`]: false }));
		}
	};

	const getStatusBadge = (status) => {
		const map = {
			PENDING: { variant: "secondary", icon: Clock, text: "Pending" },
			REVIEWED: { variant: "outline", icon: AlertCircle, text: "In Review" },
			ACCEPTED: { variant: "default", icon: CheckCircle, text: "Accepted" },
			REJECTED: { variant: "destructive", icon: XCircle, text: "Rejected" },
		};
		const s = map[status] || map.PENDING;
		const Icon = s.icon;
		return (
			<Badge variant={s.variant} className="flex items-center gap-1">
				<Icon className="w-3 h-3" />
				{s.text}
			</Badge>
		);
	};

	if (loading) return <Loader />;

	return (
		<div className="min-h-screen py-6 sm:py-8 lg:py-12 relative bg-transparent">
			<AnimatedBackground />
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-8 lg:mb-12">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
								My Dashboard
							</h1>
							<p className="text-base sm:text-lg text-muted-foreground">
								Track your applications and manage saved jobs
							</p>
						</div>
						<Button
							onClick={() => navigate("/")}
							className="w-full sm:w-auto gap-2 shadow-lg hover:shadow-xl transition-all"
						>
							<Briefcase className="w-4 h-4" />
							Browse Jobs
						</Button>
					</div>
				</div>

				{/* Error Alert */}
				{error && (
					<Alert variant="destructive" className="mb-6 shadow-md">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				{/* Quick Stats */}
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 lg:mb-12">
					{/* Total Applications */}
					<Card className="border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-lg group">
						<CardContent className="pt-6 pb-5">
							<div className="flex flex-col gap-3">
								<div className="flex items-center justify-between">
									<div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
										<Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
									</div>
									<TrendingUp className="w-4 h-4 text-primary opacity-50" />
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1">
										Total Applications
									</p>
									<p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
										{applications.length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Pending */}
					<Card className="border-2 border-yellow-200/50 dark:border-yellow-800/50 hover:border-yellow-300 dark:hover:border-yellow-700 transition-all duration-300 hover:shadow-lg group">
						<CardContent className="pt-6 pb-5">
							<div className="flex flex-col gap-3">
								<div className="flex items-center justify-between">
									<div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-yellow-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
										<Clock className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 dark:text-yellow-500" />
									</div>
									<div className="px-2 py-1 rounded-full bg-yellow-100 text-[10px] font-semibold text-yellow-700 dark:text-yellow-400">
										PENDING
									</div>
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1">
										Pending Review
									</p>
									<p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
										{applications.filter((a) => a.status === "PENDING").length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Accepted */}
					<Card className="border-2 border-green-200/50 dark:border-green-800/50 hover:border-green-300 dark:hover:border-green-700 transition-all duration-300 hover:shadow-lg group">
						<CardContent className="pt-6 pb-5">
							<div className="flex flex-col gap-3">
								<div className="flex items-center justify-between">
									<div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
										<CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-500" />
									</div>
									<div className="px-2 py-1 rounded-full bg-green-100 text-[10px] font-semibold text-green-700 dark:text-green-400">
										SUCCESS
									</div>
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1">
										Accepted
									</p>
									<p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
										{applications.filter((a) => a.status === "ACCEPTED").length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Saved Jobs */}
					<Card className="border-2 border-red-200/50 dark:border-red-800/50 hover:border-red-300 dark:hover:border-red-700 transition-all duration-300 hover:shadow-lg group">
						<CardContent className="pt-6 pb-5">
							<div className="flex flex-col gap-3">
								<div className="flex items-center justify-between">
									<div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-red-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
										<Heart className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 dark:text-red-500" />
									</div>
									<Heart className="w-4 h-4 text-red-500 opacity-50 fill-red-500" />
								</div>
								<div>
									<p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1">
										Saved Jobs
									</p>
									<p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
										{savedJobs.length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Tabs */}
				<Tabs defaultValue="applications" className="w-full">
					<TabsList className="mb-6 w-full sm:w-auto inline-flex h-11 sm:h-10 items-center justify-center rounded-lg p-1 text-muted-foreground backdrop-blur-sm grid grid-cols-2 gap-1.5 sm:gap-2">
						<TabsTrigger
							value="applications"
							className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm h-9 w-full gap-1.5"
						>
							<Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 pointer-events-none" />
							<span className="hidden sm:inline pointer-events-none">
								Applications
							</span>
							<span className="sm:hidden pointer-events-none">Apps</span>
							<span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold pointer-events-none">
								{applications.length}
							</span>
						</TabsTrigger>
						<TabsTrigger
							value="saved"
							className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm h-9 w-full gap-1.5"
						>
							<Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 pointer-events-none" />
							<span className="pointer-events-none">Saved</span>
							<span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold pointer-events-none">
								{savedJobs.length}
							</span>
						</TabsTrigger>
					</TabsList>

					{/* Applications Tab */}
					<TabsContent value="applications" className="space-y-4">
						{applications.length === 0 ? (
							<Card className="border-2 border-dashed border-muted-foreground/20">
								<CardContent className="text-center py-16 sm:py-20">
									<div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mb-6">
										<Briefcase className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
									</div>
									<p className="text-lg sm:text-xl font-semibold text-foreground mb-2">
										No applications yet
									</p>
									<p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-md mx-auto">
										Start applying to jobs and track your progress here
									</p>
									<Button
										onClick={() => navigate("/")}
										size="lg"
										className="gap-2 shadow-lg"
									>
										<Briefcase className="w-4 h-4" />
										Browse Jobs
									</Button>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 gap-4">
								{applications.map((app) => {
									const job = app.job;
									if (!job) {
										return (
											<Card
												key={app.id}
												className="border-2 border-destructive/20"
											>
												<CardContent className="text-center py-6">
													<XCircle className="w-12 h-12 mx-auto text-destructive mb-3" />
													<p className="text-muted-foreground">
														This job is no longer available.
													</p>
												</CardContent>
											</Card>
										);
									}

									return (
										<Card
											key={app.id}
											className="border-2 hover:border-primary/30 hover:shadow-xl transition-all duration-300 group"
										>
											<CardContent className="p-4 sm:p-6">
												<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
													{/* Left Content */}
													<div className="flex items-start gap-4 flex-1">
														<Avatar className="w-12 h-12 rounded-xl border bg-white shadow-sm overflow-hidden shrink-0">
															{(job.companyLogo || job.recruiter?.logoUrl) && (
																<img 
																	src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
																	alt={job.companyName}
																	className="w-full h-full object-contain p-1"
																/>
															)}
															<AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-black text-sm uppercase">
																{job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
															</AvatarFallback>
														</Avatar>
														<div className="flex-1 min-w-0 space-y-3">
															<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
																<h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors truncate">
																	{(job.id || job.jobId) ? (
																		<Link to={`/jobs/${job.id || job.jobId}`}>{job.title}</Link>
																	) : (
																		<span>{job.title}</span>
																	)}
																</h3>
																<div className="flex items-center gap-2">
																	{getStatusBadge(app.status)}
																	{(job.status === "EXPIRED" || (job.deadline && new Date(job.deadline) < new Date())) && (
																		<Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
																			<Clock className="w-3 h-3" />
																			Expired
																		</Badge>
																	)}
																</div>
															</div>

															<div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
																<div className="flex items-center gap-1.5">
																	<Building2 className="w-4 h-4 shrink-0" />
																	<span className="truncate">
																		{job.companyName ||
																			job.recruiter?.companyName ||
																			"Unknown Company"}
																	</span>
																</div>
																<div className="flex items-center gap-1.5">
																	<Calendar className="w-4 h-4 shrink-0" />
																	<span>
																		{new Date(app.appliedAt).toLocaleDateString()}
																	</span>
																</div>
																<div className="flex items-center gap-1.5">
																	<MapPin className="w-4 h-4 shrink-0" />
																	<span className="truncate">{job.location}</span>
																</div>
															</div>

															{(job.salaryMin || job.salaryMax) && (
																<div className="flex items-center gap-2 text-sm font-semibold text-primary">
																	<div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center">
																		💰
																	</div>
																	{job.salaryMin
																		? `${job.salaryMin.toLocaleString()} – ${job.salaryMax.toLocaleString()} ETB`
																		: "Competitive Salary"}
																</div>
															)}
														</div>
													</div>

													{/* Action Buttons */}
													<div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-2 lg:w-auto">
														{(job.id || job.jobId) && (
															<Link
																to={`/jobs/${job.id || job.jobId}`}
																className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors text-center"
															>
																View Details
															</Link>
														)}

														{app.status === "PENDING" && (
															<ConfirmDialog
																title="Withdraw Application?"
																description="This action cannot be undone. Your application will be permanently removed."
																onConfirm={() => handleWithdraw(app.id)}
																trigger={
																	<Button
																		variant="destructive"
																		size="sm"
																		disabled={actionLoading[app.id]}
																		className="w-full sm:w-auto gap-2"
																	>
																		{actionLoading[app.id] ? (
																			<Loader2 className="w-4 h-4 animate-spin" />
																		) : (
																			<Trash2 className="w-4 h-4" />
																		)}
																		<span>Withdraw</span>
																	</Button>
																}
															/>
														)}
													</div>
												</div>
											</CardContent>
										</Card>
									);
								})}
							</div>
						)}
					</TabsContent>

					{/* Saved Jobs Tab */}
					<TabsContent value="saved" className="space-y-4">
						{savedJobs.length === 0 ? (
							<Card className="border-2 border-dashed border-muted-foreground/20">
								<CardContent className="text-center py-16 sm:py-20">
									<div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mb-6">
										<Heart className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
									</div>
									<p className="text-lg sm:text-xl font-semibold text-foreground mb-2">
										No saved jobs yet
									</p>
									<p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-md mx-auto">
										Save interesting jobs to come back to later
									</p>
									<Button
										onClick={() => navigate("/")}
										size="lg"
										className="gap-2 shadow-lg"
									>
										<Briefcase className="w-4 h-4" />
										Explore Jobs
									</Button>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 gap-4">
								{savedJobs.map((job) => (
									<Card
										key={job.id || job.jobId}
										className="border-2 hover:border-primary/30 hover:shadow-xl transition-all duration-300 group cursor-pointer"
										onClick={() => {
											const id = job.id || job.jobId;
											if (id) navigate(`/jobs/${id}`);
										}}
									>
										<CardContent className="p-4 sm:p-6">
											<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
												{/* Left Content */}
												<div className="flex items-start gap-4 flex-1">
													<Avatar className="w-12 h-12 rounded-xl border bg-white shadow-sm overflow-hidden shrink-0">
														{(job.companyLogo || job.recruiter?.logoUrl) && (
															<img 
																src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
																alt={job.companyName}
																className="w-full h-full object-contain p-1"
															/>
														)}
														<AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-black text-sm uppercase">
															{job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
														</AvatarFallback>
													</Avatar>
													<div className="flex-1 min-w-0 space-y-3">
														<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
															<h3 className="text-lg sm:text-xl font-bold hover:text-primary transition-colors">
																{job.title || "Job not available"}
															</h3>
																<div className="flex items-center gap-2">
																	<Badge className="bg-red-50 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 border flex items-center gap-1.5 w-fit">
																		<Heart className="w-3 h-3 fill-current" />
																		<span className="hidden sm:inline">Saved</span>
																	</Badge>
																	{(job.status === "EXPIRED" || (job.deadline && new Date(job.deadline) < new Date())) && (
																		<Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
																			<Clock className="w-3 h-3" />
																			Expired
																		</Badge>
																	)}
																</div>
															</div>

														<div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
															<div className="flex items-center gap-1.5">
																<Building2 className="w-4 h-4 shrink-0" />
																<span className="truncate">
																	{job.companyName ||
																		job.recruiter?.companyName ||
																		"Unknown Company"}
																</span>
															</div>
															<div className="flex items-center gap-1.5">
																<MapPin className="w-4 h-4 shrink-0" />
																<span className="truncate">
																	{job.location || "Unknown"}
																</span>
															</div>
															<div className="flex items-center gap-1.5">
																<Briefcase className="w-4 h-4 shrink-0" />
																<span>{job.type || "Full-time"}</span>
															</div>
														</div>

														{(job.salaryMin || job.salaryMax) && (
															<div className="flex items-center gap-2 text-sm font-semibold text-primary">
																<div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center">
																	💰
																</div>
																{job.salaryMin
																	? `${job.salaryMin.toLocaleString()} – ${job.salaryMax.toLocaleString()} ETB`
																	: "Competitive Salary"}
															</div>
														)}
													</div>
												</div>

												{/* Action Buttons */}
												<div
													className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-2 lg:w-auto"
													onClick={(e) => e.stopPropagation()}
												>
													<Button
														size="sm"
														onClick={(e) => {
															e.stopPropagation();
															const id = job.id || job.jobId;
															if (id) navigate(`/jobs/${id}`);
														}}
														className="w-full sm:w-auto gap-2"
													>
														<Eye className="w-4 h-4" />
														<span>View Details</span>
													</Button>

													<ConfirmDialog
														title="Remove Saved Job?"
														description="This job will be removed from your saved list. You can save it again anytime."
														onConfirm={() => handleRemoveSaved(job.id)}
														trigger={
															<Button
																variant="outline"
																size="sm"
																disabled={actionLoading[`save-${job.id}`]}
																className="w-full sm:w-auto gap-2"
																onClick={(e) => e.stopPropagation()}
															>
																{actionLoading[`save-${job.id}`] ? (
																	<Loader2 className="w-4 h-4 animate-spin" />
																) : (
																	<Trash2 className="w-4 h-4" />
																)}
																<span>Remove</span>
															</Button>
														}
													/>
												</div>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
